<script setup>
import { computed } from 'vue';
import { messageStamp } from 'shared/helpers/timeHelper';
import { formatDelay } from 'dashboard/helper/automationHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { DATE_REACHED_EVENT } from './dateTrigger';
import { useDateTriggerSummary } from './useDateTriggerSummary';

const props = defineProps({
  automation: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'toggle',
  'edit',
  'delete',
  'clone',
  'history',
  'runNow',
]);

const { dateSummary, lastRunAlert, lastRunText } = useDateTriggerSummary();

const dateTrigger = computed(() =>
  props.automation.event_name === DATE_REACHED_EVENT
    ? props.automation.date_trigger
    : null
);

const summary = computed(() => dateSummary(dateTrigger.value));
const runAlert = computed(() => lastRunAlert(dateTrigger.value?.last_run));
const runText = computed(() => lastRunText(dateTrigger.value?.last_run));

const readableDate = date => messageStamp(new Date(date), 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date), 'LLL d, yyyy hh:mm a');

const automationActive = computed({
  get: () => props.automation.active,
  set: active => {
    const { id, name } = props.automation;
    emit('toggle', {
      id,
      name,
      status: !active,
    });
  },
});
</script>

<template>
  <BaseTableRow :item="automation">
    <template #default>
      <BaseTableCell class="max-w-0 w-full">
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-body-main text-n-slate-12 truncate">
            {{ automation.name }}
          </span>
          <span
            v-if="automation.execution_delay"
            class="text-xs px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11 whitespace-nowrap flex-shrink-0"
          >
            {{
              $t('AUTOMATION.LIST.DELAY_BADGE', {
                delay: formatDelay(automation.execution_delay),
              })
            }}
          </span>
          <div class="w-px h-3 rounded-lg bg-n-weak flex-shrink-0" />
          <span class="text-body-main text-n-slate-11 truncate">
            {{ automation.description }}
          </span>
        </div>
        <div
          v-if="dateTrigger"
          data-test="date-trigger-summary"
          class="flex items-center gap-2 min-w-0 mt-1"
        >
          <span
            class="text-xs px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11 whitespace-nowrap flex-shrink-0"
          >
            {{ $t('AUTOMATION.DATE_TRIGGER.LIST.BADGE') }}
          </span>
          <span class="text-xs text-n-slate-11 truncate">
            {{ summary }}
          </span>
          <span
            v-if="runAlert"
            data-test="date-trigger-alert"
            class="flex items-center gap-1 text-xs text-n-ruby-11 whitespace-nowrap flex-shrink-0"
          >
            <Icon icon="i-lucide-triangle-alert" class="size-3.5" />
            {{ runAlert }}
          </span>
          <span
            v-else-if="runText"
            data-test="date-trigger-last-run"
            class="text-xs text-n-slate-10 whitespace-nowrap flex-shrink-0"
          >
            {{ runText }}
          </span>
        </div>
      </BaseTableCell>

      <BaseTableCell>
        <ToggleSwitch v-model="automationActive" />
      </BaseTableCell>

      <BaseTableCell :title="readableDateWithTime(automation.created_on)">
        <span class="text-body-main text-n-slate-12 whitespace-nowrap">
          {{ readableDate(automation.created_on) }}
        </span>
      </BaseTableCell>

      <BaseTableCell align="end">
        <div class="flex gap-3 justify-end flex-shrink-0">
          <template v-if="dateTrigger">
            <Button
              v-tooltip.top="$t('AUTOMATION.DATE_TRIGGER.HISTORY.BUTTON')"
              data-test="date-trigger-history"
              icon="i-lucide-history"
              slate
              sm
              :is-loading="loading"
              @click="emit('history', automation)"
            />
            <Button
              v-if="automation.active"
              v-tooltip.top="$t('AUTOMATION.DATE_TRIGGER.RUN_NOW.BUTTON')"
              data-test="date-trigger-run-now"
              icon="i-lucide-play"
              slate
              sm
              :is-loading="loading"
              @click="emit('runNow', automation)"
            />
          </template>
          <Button
            v-tooltip.top="$t('AUTOMATION.FORM.EDIT')"
            icon="i-woot-edit-pen"
            slate
            sm
            :is-loading="loading"
            @click="$emit('edit', automation)"
          />
          <Button
            v-tooltip.top="$t('AUTOMATION.CLONE.TOOLTIP')"
            icon="i-woot-clone"
            sm
            slate
            :is-loading="loading"
            @click="$emit('clone', automation)"
          />
          <Button
            v-tooltip.top="$t('AUTOMATION.FORM.DELETE')"
            :is-loading="loading"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="$emit('delete', automation)"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
